import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { FileMap, Mode } from "@buildlab/shared";
import { Check, Close, Eye, History, Pencil } from "./icons";
import { api, ApiError, runTurn, type AssignmentInfo, type CardFocus, type DiffStat, type InterviewQuestion, type PastTurn, type PlanDetails, type TurnEvent, type TurnResultEvent } from "./api";
import { Lion } from "./Lion";
import { emitStep } from "./mission-steps";
import { TalkButton } from "./TalkButton";
import { useFeature } from "./features";
import { SurpriseMe } from "./Surprise";
import { CoinCounter } from "./CoinCounter";
import { useMe, useRefreshCoins } from "./me";
import { replyWords } from "@buildlab/shared/narration";
import { Narrate } from "./Narrate";

// The agent pane (PRD §5.1, §5.2): mode switch, the student's messages, each step the agent takes,
// its reply, and "What changed". Plan mode ends with a plan to approve, edit or reject.
// Today's mission lives in the bar above the workspace (Mission.tsx), so the column is the chat:
// a slim message box (one row that grows with what's typed) with Send beside it, and the "an AI,
// not a person" note always showing in the header.

/** A card picked in the code view; `nonce` changes on every pick so the pane reacts to repeats. */
export interface PickedCard extends CardFocus {
  mode: "build" | "ask";
  nonce: number;
}

interface Exchange {
  id: number;
  mode: Mode;
  message: string;
  card?: string;
  errorsSent: number;
  steps: { label: string; tool: string }[];
  text: string;
  result?: TurnResultEvent;
  refusal?: boolean;
  error?: string;
  /** Why the helper turned the message away before starting: a limit, or screening (guardrails.ts). */
  errorKind?: "limit" | "blocked";
  planState?: "open" | "approved" | "rejected" | "past";
  /** Loaded from the server after a reload. */
  past?: boolean;
  /** The student undid this turn's changes. */
  undone?: boolean;
  /** Answers to this turn's planning interview, once sent. */
  answers?: string[];
}

/** Lessons built around the bug-report formula (the Demo and Lesson 4): Build mode suggests it. */
const BUG_REPORT_LESSONS = [0, 4];

const MODE_INFO: Record<Mode, { label: string; hint: string; prompt: string; placeholder: string }> = {
  build: { label: "Build", hint: "The helper can change your files.", prompt: "Tell the helper what to build", placeholder: "I did ___. I expected ___. Instead ___." },
  plan: { label: "Plan", hint: "The helper writes a plan first. Nothing changes until you approve it.", prompt: "Describe what you want to make", placeholder: "Describe your game. You'll get a plan to approve." },
  ask: { label: "Ask", hint: "The helper explains your code. It won't change anything.", prompt: "Ask about your code", placeholder: "Like: what does line 12 do?" },
};

function StepIcon({ tool }: { tool: string }) {
  const edit = /edit|write|delete/.test(tool);
  const done = tool === "finish" || tool === "propose_plan";
  return (
    <span className="step-icon" aria-hidden="true">
      {edit ? <Pencil size={12} width={2.5} /> : done ? <Check size={12} width={3} /> : <Eye size={12} width={2.5} />}
    </span>
  );
}

let exchangeId = 0;

/** A turn saved on the server, shown like one from this visit (its plan can't be approved again). */
function fromPast(t: PastTurn): Exchange {
  const tool = (label: string) => (/^(Editing|Writing|Deleting)/.test(label) ? "edit_file" : label === "Finishing up" ? "finish" : label === "Writing a plan" ? "propose_plan" : "read_file");
  return {
    id: ++exchangeId,
    mode: t.mode,
    message: t.message,
    errorsSent: 0,
    steps: t.steps.map((label) => ({ label, tool: tool(label) })),
    text: t.finalText,
    result: { status: t.status, finalText: t.finalText, summary: t.summary, tryThis: t.tryThis, plan: t.plan, diff: [], files: {}, checkpointId: t.checkpointId },
    planState: t.plan ? "past" : undefined,
    past: true,
  };
}

export function AgentPane({
  projectId,
  assignment,
  errors,
  attachErrors,
  onAttachErrors: setAttachErrors,
  onFiles,
  onTurnStart,
  onRunningChange,
  onFileChange,
  picked,
  onClearPicked,
  draftRequest,
  allowedModes,
  golf = false,
  bot = false,
}: {
  projectId: string;
  assignment: AssignmentInfo | null;
  errors: string[];
  attachErrors: boolean;
  onAttachErrors: (v: boolean) => void;
  onFiles: (files: FileMap) => void;
  onTurnStart: () => Promise<void>;
  onRunningChange: (running: boolean) => void;
  /** A file the helper just changed, while the turn is still running (null = deleted). */
  onFileChange: (path: string, content: string | null) => void;
  picked: PickedCard | null;
  onClearPicked: () => void;
  /** Text to put in the message box (e.g. from the project map); `nonce` changes per request. */
  draftRequest?: { text: string; nonce: number } | null;
  /** Modes unlocked in this lesson (D46). Locked modes aren't shown at all. */
  allowedModes?: Mode[];
  /** Prompt Golf (D53): no Surprise me, and the empty chat and box say every word counts. */
  golf?: boolean;
  /** A Bot Arena bot (decision 99): the box suggests a strategy instead of a look. */
  bot?: boolean;
}) {
  const assignmentModes: Mode[] = assignment?.modes?.length ? (["build", "plan", "ask"] as Mode[]).filter((m) => assignment.modes.includes(m)) : ["build", "plan", "ask"];
  const modes: Mode[] = allowedModes ? assignmentModes.filter((m) => allowedModes.includes(m)) : assignmentModes;
  const [mode, setMode] = useState<Mode>(assignment?.startMode && modes.includes(assignment.startMode) ? assignment.startMode : modes[0]!);
  const [draft, setDraft] = useState("");
  const surpriseOn = useFeature("surpriseMe");
  const coins = useMe()?.coins; // students only (teachers have no limit)
  const refreshCoins = useRefreshCoins();
  const [feed, setFeed] = useState<Exchange[]>([]);
  const [running, setRunning] = useState(false);
  const abort = useRef<AbortController | null>(null);
  const feedEnd = useRef<HTMLDivElement>(null);

  // Braces matter: newer browsers return a Promise from scrollIntoView, and an effect must return
  // nothing or a cleanup function (returning the Promise crashed the app on the next message).
  useEffect(() => {
    if (feed.length) feedEnd.current?.scrollIntoView({ block: "end" });
  }, [feed]);
  // Earlier exchanges come back after a reload (newest 20), so nothing a student did disappears.
  useEffect(() => {
    let cancelled = false;
    api
      .turns(projectId)
      .then(({ turns }) => {
        if (cancelled || turns.length === 0) return;
        setFeed((f) => (f.length ? f : turns.map(fromPast)));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [projectId]);
  useEffect(() => {
    if (!picked) return;
    if (modes.includes(picked.mode)) setMode(picked.mode);
    document.getElementById("message")?.focus();
  }, [picked?.nonce]);
  useEffect(() => {
    if (!draftRequest) return;
    setDraft(draftRequest.text);
    document.getElementById("message")?.focus();
  }, [draftRequest?.nonce]);
  useEffect(() => {
    if (errors.length === 0) setAttachErrors(false);
  }, [errors.length]);

  const update = (id: number, fn: (x: Exchange) => Exchange) => setFeed((f) => f.map((x) => (x.id === id ? fn(x) : x)));

  async function send(turnMode: Mode, message: string, approvedPlan?: string[]) {
    const sentErrors = attachErrors ? errors : [];
    const focus = picked && !approvedPlan ? { title: picked.title, file: picked.file, startLine: picked.startLine, endLine: picked.endLine } : undefined;
    const ex: Exchange = { id: ++exchangeId, mode: turnMode, message: message || "Go ahead with the plan.", card: focus?.title, errorsSent: sentErrors.length, steps: [], text: "" };
    if (focus) onClearPicked();
    setFeed((f) => [...f, ex]);
    setDraft("");
    setAttachErrors(false);
    setRunning(true);
    onRunningChange(true);
    await onTurnStart(); // unsaved edits reach the server before the helper reads the files
    const controller = new AbortController();
    abort.current = controller;
    try {
      await runTurn(
        projectId,
        { mode: turnMode, message, errors: sentErrors.length ? sentErrors : undefined, approvedPlan, focus },
        (e: TurnEvent) => {
          if (e.type === "step") update(ex.id, (x) => ({ ...x, steps: [...x.steps, { label: e.label, tool: e.tool }] }));
          else if (e.type === "file") onFileChange(e.path, e.content);
          else if (e.type === "message_delta") update(ex.id, (x) => ({ ...x, text: x.text + e.text }));
          else if (e.type === "refusal") update(ex.id, (x) => ({ ...x, refusal: true }));
          else if (e.type === "error") update(ex.id, (x) => ({ ...x, error: e.message }));
          else if (e.type === "result") {
            update(ex.id, (x) => ({ ...x, result: e, planState: e.plan ? "open" : undefined }));
            onFiles(e.files);
            if (message) emitStep({ kind: "sent", mode: turnMode }); // a mission step (D45); approving a plan isn't a message
          }
        },
        controller.signal,
      );
    } catch (err) {
      const stopped = controller.signal.aborted;
      const kind = err instanceof ApiError ? err.kind : undefined;
      update(ex.id, (x) => ({ ...x, errorKind: kind, error: stopped ? "Stopped. Any changes so far are kept; you can restore the checkpoint from before this message." : (err as Error).message }));
      // Screening turned it away: give the words back so the student can change them.
      if (kind === "blocked" && message) setDraft((d) => d || message);
    } finally {
      setRunning(false);
      onRunningChange(false);
      abort.current = null;
      if (coins) refreshCoins(); // the turn used a coin (or was refused): show what's left
    }
  }

  // The message box starts one row tall and grows with what's typed (or the placeholder) up to about
  // six rows, then scrolls.
  const box = useRef<HTMLTextAreaElement>(null);
  const placeholderKey = `${mode}:${assignment?.lesson ?? ""}`;
  const fitBox = () => {
    const el = box.current;
    if (!el) return;
    const css = getComputedStyle(el);
    const line = parseFloat(css.lineHeight) || 22;
    const borders = parseFloat(css.borderTopWidth) + parseFloat(css.borderBottomWidth);
    const max = Math.round(line * 6 + parseFloat(css.paddingTop) + parseFloat(css.paddingBottom) + borders);
    el.style.height = "auto";
    const want = el.scrollHeight + borders;
    el.style.height = `${Math.min(want, max)}px`;
    el.style.overflowY = want > max ? "auto" : "hidden";
  };
  useLayoutEffect(fitBox, [draft, placeholderKey, picked?.nonce]);
  // A new width (a resized window, Large text) wraps the words differently.
  useEffect(() => {
    const el = box.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    let width = el.offsetWidth;
    const ro = new ResizeObserver(() => {
      if (el.offsetWidth === width) return;
      width = el.offsetWidth;
      fitBox();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const submit = () => {
    const text = draft.trim();
    if (!text || running) return;
    void send(mode, text);
  };

  return (
    <section className={`agent${feed.length ? " has-chat" : ""}`} aria-label="Helper">
      <div className="pane-head">
        <div className="helper-id">
          <h2 className="eyebrow">Helper</h2>
          {/* Always on screen for students (guardrails): who they're talking to. */}
          <p className="ai-note" title="BuildLab's helper is an AI, not a person.">
            An AI, not a person
          </p>
        </div>
        {/* surpriseMe: twists for Build mode only (Surprise.tsx); its ideas open under this header */}
        {mode === "build" && surpriseOn && !golf && (
          <SurpriseMe
            projectId={projectId}
            disabled={running}
            onPick={(message) => {
              setDraft(message);
              document.getElementById("message")?.focus();
            }}
          />
        )}
        <div className="modes" role="radiogroup" aria-label="Mode">
          {(["build", "plan", "ask"] as Mode[]).filter((m) => !allowedModes || modes.includes(m)).map((m) => (
            <button
              key={m}
              type="button"
              role="radio"
              aria-checked={mode === m}
              disabled={!modes.includes(m) || running}
              className={mode === m ? "on" : ""}
              onClick={() => setMode(m)}
              title={modes.includes(m) ? MODE_INFO[m].hint : "Turned off for this assignment"}
            >
              {MODE_INFO[m].label}
            </button>
          ))}
        </div>
      </div>

      <div className="feed" aria-live="polite">
        {feed.length === 0 && picked && (
          <div className="empty picked-empty">
            <p className="picked-title">{picked.mode === "ask" ? "Ask about one card" : "Change one card at a time"}</p>
            <p className="muted">
              {picked.mode === "ask"
                ? "You picked a card, so the helper explains that part of your program."
                : "You picked a card, so the helper only works on that part of your program. Small, clear requests work best."}
            </p>
          </div>
        )}
        {feed.length === 0 && !picked && (
          <div className="empty helper-empty">
            <Lion pose="wave" size={34} />
            {golf ? (
              <p>
                <b>Every message is a stroke.</b> The helper can't see the app you're copying. It only knows what you tell it.
              </p>
            ) : (
              <p>
                <b>{MODE_INFO[mode].label} mode.</b> {MODE_INFO[mode].hint}
              </p>
            )}
          </div>
        )}
        {feed.map((x) => (
          <article key={x.id} className="exchange">
            <div className="you">
              <span className="mode-tag">
                {MODE_INFO[x.mode].label}
                {x.card && <span className="card-tag"> · Card: {x.card}</span>}
              </span>
              <p>{x.message}</p>
              {x.errorsSent > 0 && <span className="attached">+ {x.errorsSent} console error{x.errorsSent === 1 ? "" : "s"}</span>}
            </div>
            {x.steps.length > 0 && (
              <ol className="steps">
                {x.steps.map((s, i) => (
                  <li key={i} className={`step ${s.tool}`}>
                    <StepIcon tool={s.tool} />
                    {s.label}
                  </li>
                ))}
                {!x.result && !x.error && running && <li className="step working">Working…</li>}
              </ol>
            )}
            {x.steps.length === 0 && !x.result && !x.error && running && <p className="step working">Thinking…</p>}
            <Reply
              x={x}
              canUndo={!running}
              onUndo={async () => {
                const id = x.result?.checkpointId;
                if (!id) return;
                const { files } = await api.restore(projectId, id);
                onFiles(files);
                update(x.id, (y) => ({ ...y, undone: true }));
              }}
            />
            {x.result?.interview && (
              <QuestionsCard
                questions={x.result.interview}
                answers={x.answers}
                disabled={running || !!x.past}
                onSubmit={(answers) => {
                  update(x.id, (y) => ({ ...y, answers }));
                  const text = x.result!.interview!.map((q, i) => `${i + 1}. ${q.question} ${answers[i]}`).join("\n");
                  void send("plan", `Here are my answers:\n${text}`);
                }}
              />
            )}
            {x.result?.plan && (
              <PlanCard
                plan={x.result.plan}
                state={x.planState ?? "open"}
                disabled={running}
                onApprove={async (steps) => {
                  update(x.id, (y) => ({ ...y, planState: "approved" }));
                  emitStep({ kind: "approvedPlan" });
                  // The approved plan goes into PROJECT.md first, so the Project map fills itself in.
                  const d = x.result!.plan!.details;
                  try {
                    const { files } = await api.writePlan(projectId, { forWhom: d?.forWhom, later: d?.later ?? [], screens: d?.screens ?? [], tests: d?.tests ?? [], mvp: steps });
                    onFiles(files);
                  } catch {
                    // The build still goes ahead; the plan is in the conversation either way.
                  }
                  void send("build", "", steps);
                }}
                onReject={() => update(x.id, (y) => ({ ...y, planState: "rejected" }))}
              />
            )}
          </article>
        ))}
        <div ref={feedEnd} />
      </div>

      <form
        className="composer"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        {errors.length > 0 && (
          <label className="errors-toggle">
            <input id="attach-errors" type="checkbox" checked={attachErrors} onChange={(e) => setAttachErrors(e.target.checked)} disabled={running} />
            Send {errors.length} console error{errors.length === 1 ? "" : "s"} to the helper
          </label>
        )}
        {picked && (
          <div className={`card-chip k-${picked.mode}`}>
            Card: {picked.title}
            <button type="button" aria-label="Remove card" onClick={onClearPicked} disabled={running}>
              <Close size={10} width={3} />
            </button>
          </div>
        )}
        {/* The box's name, for screen readers; the placeholder shows what to write. */}
        <label htmlFor="message" className="sr-only">
          {picked ? (mode === "ask" ? "What do you want to know about this card?" : "What should this card do?") : MODE_INFO[mode].prompt}
        </label>
        <div className="composer-row">
          <textarea
            id="message"
            ref={box}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
            placeholder={golf ? "Say exactly what to build: what, when, and what it looks like." : bot && mode === "build" ? "Like: go for the closest coin, and walk around walls." : mode === "build" && !BUG_REPORT_LESSONS.includes(assignment?.lesson ?? -1) ? "Like: make the button bigger and blue." : MODE_INFO[mode].placeholder}
            rows={1}
            disabled={running}
          />
          <TalkButton draft={draft} setDraft={setDraft} disabled={running} />
          {coins && <CoinCounter left={coins.left} />}
          {running ? (
            <button type="button" className="stop" onClick={() => abort.current?.abort()}>
              Stop
            </button>
          ) : (
            <button type="submit" className="primary" disabled={!draft.trim()}>
              Send
            </button>
          )}
        </div>
      </form>
    </section>
  );
}

function Reply({ x, canUndo, onUndo }: { x: Exchange; canUndo: boolean; onUndo: () => Promise<void> }) {
  const [undoing, setUndoing] = useState(false);
  const r = x.result;
  if (x.refusal) return <p className="reply notice">The helper can't help with that one. Try asking for something else for your project.</p>;
  return (
    <>
      {x.text && !(r?.status === "done" && r.summary.length) && <p className="reply">{x.text}</p>}
      {r?.status === "done" && (
        <div className="done">
          <h3>What I changed</h3>
          <ul>
            {r.summary.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
          <DiffList diff={r.diff} />
          {r.checkpointId ? (
            x.undone ? (
              <p className="undone">Undone. Your project is back to how it was before this message.</p>
            ) : (
              <button
                type="button"
                className="outline small undo"
                disabled={!canUndo || undoing}
                title="Put your project back the way it was before this message"
                onClick={async () => {
                  setUndoing(true);
                  try {
                    await onUndo();
                  } finally {
                    setUndoing(false);
                  }
                }}
              >
                <History size={14} />
                {undoing ? "Undoing…" : "Undo this change"}
              </button>
            )
          ) : null}
          {r.tryThis.length > 0 && (
            <>
              <h3>Try this</h3>
              <ul className="try">
                {r.tryThis.map((s, i) => (
                  <li key={i}>
                    <label>
                      <input type="checkbox" />
                      {s}
                    </label>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
      {r && (r.status === "step_limit" || r.status === "context_limit" || r.status === "error") && (
        <p className="reply notice">The helper got stuck on this one. Try a smaller request, or restore the checkpoint from before it.</p>
      )}
      {x.error && x.errorKind ? (
        <div className={`reply guard-note ${x.errorKind}`} role="status">
          <Lion pose="think" size={56} />
          <div>
            <b>{x.errorKind === "limit" ? "The helper needs a break" : "Let's change a few words"}</b>
            <p>{x.error}</p>
          </div>
        </div>
      ) : (
        x.error && <p className="reply notice">{x.error}</p>
      )}
      {/* Read aloud with the Chromebook's own voice only (decision 90, D48); the words stay on the device. */}
      {r && (
        <Narrate
          source="reply"
          className="reply-narrate"
          text={replyWords({
            text: r.status === "done" && r.summary.length ? "" : x.text,
            changed: r.status === "done" ? r.summary : [],
            tryThis: r.status === "done" ? r.tryThis : [],
          })}
        />
      )}
    </>
  );
}

function DiffList({ diff }: { diff: DiffStat[] }) {
  if (diff.length === 0) return null;
  return (
    <ul className="diff">
      {diff.map((d) => (
        <li key={d.path}>
          <code>{d.path}</code>
          {d.status === "added" && <span className="tag">new</span>}
          {d.status === "deleted" && <span className="tag">deleted</span>}
          <span className="plus">+{d.added}</span>
          <span className="minus">−{d.removed}</span>
        </li>
      ))}
    </ul>
  );
}

function PlanCard({
  plan,
  state,
  disabled,
  onApprove,
  onReject,
}: {
  plan: { steps: string[]; questions: string[]; details?: PlanDetails };
  state: "open" | "approved" | "rejected" | "past";
  disabled: boolean;
  onApprove: (steps: string[]) => void;
  onReject: () => void;
}) {
  const [steps, setSteps] = useState(plan.steps);
  const open = state === "open";
  return (
    <div className="plan">
      <h3>The plan</h3>
      <ol>
        {steps.map((s, i) => (
          <li key={i}>
            {open ? (
              <input
                id={`plan-step-${i}`}
                value={s}
                onChange={(e) => setSteps((all) => all.map((v, j) => (j === i ? e.target.value : v)))}
                aria-label={`Step ${i + 1}`}
              />
            ) : (
              s
            )}
          </li>
        ))}
      </ol>
      {plan.details && <PlanExtras details={plan.details} />}
      {plan.questions.length > 0 && (
        <div className="questions">
          <b>Questions for you:</b>
          <ul>
            {plan.questions.map((q, i) => (
              <li key={i}>{q}</li>
            ))}
          </ul>
        </div>
      )}
      {open ? (
        <div className="row">
          <button type="button" className="primary" disabled={disabled} onClick={() => onApprove(steps.map((s) => s.trim()).filter(Boolean))}>
            Approve and build
          </button>
          <button type="button" className="ghost" disabled={disabled} onClick={onReject}>
            Reject
          </button>
          <span className="muted small">You can edit any step first.</span>
        </div>
      ) : (
        <p className="muted small">{state === "approved" ? "Approved." : state === "past" ? "From earlier." : "Rejected. Try describing what you want differently."}</p>
      )}
    </div>
  );
}

/** Who it's for, screens, tests and later ideas from a plan; approving writes them into PROJECT.md. */
function PlanExtras({ details }: { details: PlanDetails }) {
  if (!details.forWhom && !details.screens.length && !details.tests.length && !details.later.length) return null;
  return (
    <div className="plan-extras">
      {details.forWhom && (
        <p>
          <b>For:</b> {details.forWhom}
        </p>
      )}
      {details.screens.length > 0 && (
        <p>
          <b>Screens:</b> {details.screens.map((s) => s.name).join(" → ")}
        </p>
      )}
      {details.tests.length > 0 && (
        <div>
          <b>How you'll check it:</b>
          <ul>
            {details.tests.map((t, i) => (
              <li key={i}>{t}</li>
            ))}
          </ul>
        </div>
      )}
      {details.later.length > 0 && (
        <p>
          <b>Later:</b> {details.later.join(" · ")}
        </p>
      )}
      <p className="muted small">Approving saves this to PROJECT.md, so your Project map fills in.</p>
    </div>
  );
}

/** The planning interview, one question at a time (like a review page): pick an option or write your own. */
function QuestionsCard({
  questions,
  answers: sent,
  disabled,
  onSubmit,
}: {
  questions: InterviewQuestion[];
  answers?: string[];
  disabled: boolean;
  onSubmit: (answers: string[]) => void;
}) {
  const [step, setStep] = useState(0);
  const [picks, setPicks] = useState<string[]>(() => questions.map(() => ""));
  const [other, setOther] = useState<string[]>(() => questions.map(() => ""));
  const OTHER = "\u0000other";
  const answerFor = (i: number) => (picks[i] === OTHER ? other[i]!.trim() : picks[i]!);

  if (sent) {
    return (
      <div className="interview done-interview">
        <h3>Your answers</h3>
        <ol>
          {questions.map((q, i) => (
            <li key={i}>
              <span className="muted small">{q.question}</span>
              <br />
              {sent[i]}
            </li>
          ))}
        </ol>
      </div>
    );
  }
  const q = questions[step]!;
  const last = step === questions.length - 1;
  const ready = answerFor(step).length > 0;
  return (
    <div className="interview" role="group" aria-label="Questions from the helper">
      <div className="row between">
        <h3>A few questions first</h3>
        <span className="muted small">
          {step + 1} of {questions.length}
        </span>
      </div>
      <div className="progress-dots" aria-hidden="true">
        {questions.map((_, i) => (
          <span key={i} className={i < step ? "past" : i === step ? "on" : ""} />
        ))}
      </div>
      <p className="iq">{q.question}</p>
      <div className="ioptions" role="radiogroup" aria-label={q.question}>
        {[...q.options, OTHER].map((o) => (
          <label key={o} className={`ioption${picks[step] === o ? " on" : ""}`}>
            <input
              type="radio"
              name={`iq-${step}`}
              checked={picks[step] === o}
              disabled={disabled}
              onChange={() => setPicks((p) => p.map((v, i) => (i === step ? o : v)))}
            />
            {o === OTHER ? "Something else" : o}
          </label>
        ))}
        {picks[step] === OTHER && (
          <input
            className="iother"
            autoFocus
            placeholder="Type your answer"
            value={other[step]}
            disabled={disabled}
            onChange={(e) => setOther((p) => p.map((v, i) => (i === step ? e.target.value : v)))}
          />
        )}
      </div>
      <div className="row between">
        <button type="button" className="linklike" disabled={step === 0} onClick={() => setStep(step - 1)}>
          ← Back
        </button>
        {last ? (
          <button type="button" className="primary small" disabled={disabled || !ready} onClick={() => onSubmit(questions.map((_, i) => answerFor(i)))}>
            Send my answers
          </button>
        ) : (
          <button type="button" className="primary small" disabled={!ready} onClick={() => setStep(step + 1)}>
            Next →
          </button>
        )}
      </div>
    </div>
  );
}
